import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_BASE_URL_TOKEN, AUTH_CONFIG_TOKEN } from '../auth/auth.token';
import { ApiResponse } from '@nebula-shared/domain';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL_TOKEN);
  private readonly authConfig = inject(AUTH_CONFIG_TOKEN);

  get<T>(
    endpoint: string,
    params?: Record<string, unknown>,
    headers?: HttpHeaders,
  ): Observable<ApiResponse<T>> {
    let httpParams = new HttpParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, String(value));
        }
      });
    }

    const options: { params: HttpParams; headers?: HttpHeaders } = { params: httpParams };

    if (headers) {
      options.headers = headers;
    }

    return this.http.get<ApiResponse<T>>(`${this.apiBaseUrl}${endpoint}`, options);
  }

  post<T>(
    endpoint: string,
    body: unknown,
    params?: Record<string, unknown>,
    headers?: HttpHeaders,
  ): Observable<ApiResponse<T>> {
    const options: { params?: HttpParams; headers?: HttpHeaders } = {};

    if (params && Object.keys(params).length > 0) {
      let httpParams = new HttpParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, String(value));
        }
      });
      options.params = httpParams;
    }

    if (headers) {
      options.headers = headers;
    }

    return this.http
      .post<ApiResponse<T>>(`${this.apiBaseUrl}${endpoint}`, body, options)
      .pipe(catchError((error: unknown) => throwError(() => error)));
  }

  /**
   * POST request que retorna un Blob (para descarga de archivos)
   * @param endpoint - Endpoint de la API
   * @param body - Cuerpo de la petición
   * @param params - Parámetros de consulta
   * @param headers - Headers adicionales
   * @returns Observable<Blob>
   */
  postBlob(
    endpoint: string,
    body: unknown,
    params?: Record<string, unknown>,
    headers?: HttpHeaders,
  ): Observable<Blob> {
    const options: { params?: HttpParams; headers?: HttpHeaders; responseType: 'blob' } = {
      responseType: 'blob',
    };

    if (params && Object.keys(params).length > 0) {
      let httpParams = new HttpParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, String(value));
        }
      });
      options.params = httpParams;
    }

    if (headers) {
      options.headers = headers;
    }

    return this.http.post(`${this.apiBaseUrl}${endpoint}`, body, options).pipe(
      catchError((error: unknown) => {
        return throwError(() => error);
      }),
    );
  }

  /**
   * GET request que retorna un Blob (para descarga de archivos)
   */
  getBlob(
    endpoint: string,
    params?: Record<string, unknown>,
    headers?: HttpHeaders,
  ): Observable<Blob> {
    const options: { params?: HttpParams; headers?: HttpHeaders; responseType: 'blob' } = {
      responseType: 'blob',
    };

    if (params && Object.keys(params).length > 0) {
      let httpParams = new HttpParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, String(value));
        }
      });
      options.params = httpParams;
    }

    if (headers) {
      options.headers = headers;
    }

    return this.http.get(`${this.apiBaseUrl}${endpoint}`, options).pipe(
      catchError((error: unknown) => {
        return throwError(() => error);
      }),
    );
  }

  put<T>(endpoint: string, body: unknown, headers?: HttpHeaders): Observable<ApiResponse<T>> {
    const options = headers ? { headers } : {};
    return this.http.put<ApiResponse<T>>(`${this.apiBaseUrl}${endpoint}`, body, options);
  }

  patch<T>(endpoint: string, body: unknown): Observable<ApiResponse<T>> {
    return this.http.patch<ApiResponse<T>>(`${this.apiBaseUrl}${endpoint}`, body);
  }

  delete<T>(endpoint: string): Observable<ApiResponse<T>> {
    return this.http.delete<ApiResponse<T>>(`${this.apiBaseUrl}${endpoint}`);
  }

  getContent<T>(
    endpoint: string,
    params?: Record<string, string>,
    headers?: HttpHeaders,
  ): Observable<T> {
    return this.get<T>(endpoint, params, headers).pipe(
      map((response) => response.response.content),
    );
  }

  postContent<T>(
    endpoint: string,
    body: unknown,
    params?: Record<string, unknown>,
    headers?: HttpHeaders,
  ): Observable<T> {
    return this.post<T>(endpoint, body, params, headers).pipe(
      map((response) => response.response.content),
    );
  }

  putContent<T>(endpoint: string, body: unknown, headers?: HttpHeaders): Observable<T> {
    return this.put<T>(endpoint, body, headers).pipe(map((response) => response.response.content));
  }

  patchContent<T>(endpoint: string, body: unknown): Observable<T> {
    return this.patch<T>(endpoint, body).pipe(map((response) => response.response.content));
  }
}
